import React, { useMemo, useState } from "react";
import { Box, Typography, Link } from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import {
  mergeSourcesByUrl,
  displayLabel,
  DEFAULT_MAX_SOURCES,
} from "utils/clinicalSources";
import PdfViewerDialog from "components/common/PdfViewerDialog";

/**
 * ClinicalReferenceSources
 *
 * Lists the clinical reference / embedded RAG documents that backed an LLM
 * response and lets the user preview each one in an inline PDF viewer.
 * Self-contained: merges the two source lists and owns its viewer dialog state.
 * Renders nothing when there are no sources.
 *
 * Designed to sit at the bottom of a ChartMind step (Diagnosis, Treatment, …).
 *
 * Props:
 * - embeddedDocuments: RAG documents from the LLM response (with similarity)
 * - clinicalReferences: tag-retrieved clinical reference documents
 * - title: section heading (default "Source documents")
 * - maxSources: cap on how many documents to list
 */
const ClinicalReferenceSources = ({
  embeddedDocuments = [],
  clinicalReferences = [],
  title = "Source documents",
  maxSources = DEFAULT_MAX_SOURCES,
}) => {
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: null,
    title: null,
  });

  const sources = useMemo(
    () =>
      mergeSourcesByUrl(embeddedDocuments, clinicalReferences, maxSources),
    [embeddedDocuments, clinicalReferences, maxSources],
  );

  const handleOpenPdf = (source) => {
    if (source?.downloadURL) {
      setPdfViewer({
        open: true,
        url: source.downloadURL,
        title: displayLabel(source),
      });
    }
  };

  const handleClose = () =>
    setPdfViewer({ open: false, url: null, title: null });

  if (!sources.length) return null;

  return (
    <Box
      sx={{
        mt: 4,
        pt: 3,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {sources.map((source) => (
          <Box
            key={source.downloadURL}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <PictureAsPdf
              sx={{ fontSize: "0.9rem", color: "error.main", flexShrink: 0 }}
            />
            <Link
              component="button"
              variant="caption"
              onClick={() => handleOpenPdf(source)}
              sx={{ textAlign: "left", wordBreak: "break-word" }}
            >
              {displayLabel(source)}
            </Link>
          </Box>
        ))}
      </Box>

      {/* PDF Viewer */}
      <PdfViewerDialog
        open={pdfViewer.open}
        url={pdfViewer.url}
        title={pdfViewer.title}
        onClose={handleClose}
      />
    </Box>
  );
};

export default ClinicalReferenceSources;
